import {
  MICE_BIRTHDATE_MIN,
  MICE_PROFILE_FIELDS,
  isValidCpf,
  type MiceGender,
  type MiceProfileCheckView,
  type MiceProfileChoice,
  type MiceProfileField,
  type MiceProfileResolve,
} from '@voll-intelligence/domain';

/**
 * O que o operador fez na seção Cadastro: a escolha de cada campo a decidir e o que digitou.
 * Começa vazio: nada é pré-preenchido, nem com o valor da planilha.
 */
export interface ProfileDraft {
  choices: Partial<Record<MiceProfileField, MiceProfileChoice>>;
  firstName: string;
  lastName: string;
  /** Como está no campo, com a máscara `DD/MM/AAAA`. */
  birthdate: string;
  /** Como está no campo, com a máscara `000.000.000-00`. */
  cpf: string;
  /** O sexo escolhido no formulário; nunca presumido. */
  gender: MiceGender | null;
  /** O servidor recusou o nome da planilha sem Nome e Sobrenome (o `Nome Completo` da malha). */
  nameSplit: boolean;
}

export const EMPTY_DRAFT: ProfileDraft = {
  choices: {},
  firstName: '',
  lastName: '',
  birthdate: '',
  cpf: '',
  gender: null,
  nameSplit: false,
};

/**
 * `decide`: o campo espera a escolha (`to_complete`/`divergent`); `type`: falta o valor e só o
 * formulário resolve; `none`: nada a fazer.
 */
export type ProfileRowMode = 'decide' | 'type' | 'none';

export function rowMode(check: MiceProfileCheckView, field: MiceProfileField): ProfileRowMode {
  if (check.missing.includes(field)) return 'type';
  const { state } = check.fields[field];
  return state === 'to_complete' || state === 'divergent' ? 'decide' : 'none';
}

/**
 * O nome vai digitado quando falta, ou quando "Usar o da planilha" voltou do servidor pedindo Nome e
 * Sobrenome: a planilha trouxe o `Nome Completo` da malha, e o MICE nunca divide um nome sozinho.
 * O dos modelos (nome e sobrenome) grava num clique.
 */
export const nameTyped = (check: MiceProfileCheckView, draft: ProfileDraft): boolean =>
  rowMode(check, 'name') === 'type' ||
  (rowMode(check, 'name') === 'decide' && draft.choices.name === 'sheet' && draft.nameSplit);

/** Por que "Gravar" está desabilitado: o primeiro campo incompleto, ou nada decidido ainda. */
export type ProfileBlockReason = MiceProfileField | 'choose' | 'fill';

const digitsOf = (value: string, max: number): string => value.replaceAll(/\D/g, '').slice(0, max);

/** `12031985` → `12/03/1985`, à medida que se digita. */
export function maskBirthdate(value: string): string {
  const d = digitsOf(value, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('/');
}

/** `DD/MM/AAAA` completo, data que existe, entre 1900-01-01 e hoje → `AAAA-MM-DD`; senão `null`. */
export function birthdateIso(masked: string): string | null {
  const d = digitsOf(masked, 8);
  if (d.length !== 8) return null;
  const iso = `${d.slice(4, 8)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
  const date = new Date(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso) return null;
  return iso >= MICE_BIRTHDATE_MIN && iso <= new Date().toISOString().slice(0, 10) ? iso : null;
}

/** O erro de formato que a tela mostra: só com o campo inteiro digitado. */
export const birthdateInvalid = (masked: string): boolean =>
  digitsOf(masked, 8).length === 8 && birthdateIso(masked) === null;

export const cpfInvalid = (masked: string): boolean => {
  const d = digitsOf(masked, 11);
  return d.length === 11 && !isValidCpf(d);
};

type Typed = NonNullable<MiceProfileResolve['typed']>;

/** O digitado de cada campo: `null` = nada digitado; `false` = digitado e ainda inválido. */
const TYPED: Record<MiceProfileField, (draft: ProfileDraft) => Typed | null | false> = {
  name: (draft) => {
    const firstName = draft.firstName.trim();
    const lastName = draft.lastName.trim();
    if (firstName === '' && lastName === '') return null;
    return firstName !== '' && lastName !== '' ? { firstName, lastName } : false;
  },
  birthdate: (draft) => {
    if (draft.birthdate === '') return null;
    const birthdate = birthdateIso(draft.birthdate);
    return birthdate ? { birthdate } : false;
  },
  cpf: (draft) => {
    const cpf = digitsOf(draft.cpf, 11);
    if (cpf === '') return null;
    return isValidCpf(cpf) ? { cpf } : false;
  },
  gender: (draft) => (draft.gender ? { gender: draft.gender } : null),
};

/** A decisão de um campo: a escolha, o digitado, nada, ou `invalid` (digitado incompleto). */
function fieldDecision(
  check: MiceProfileCheckView,
  draft: ProfileDraft,
  field: MiceProfileField,
): { choice: MiceProfileChoice } | { typed: Typed } | 'invalid' | null {
  const mode = rowMode(check, field);
  const typing = field === 'name' ? nameTyped(check, draft) : mode === 'type';
  if (!typing) {
    const choice = draft.choices[field];
    return mode === 'decide' && choice ? { choice } : null;
  }
  const value = TYPED[field](draft);
  if (value === false) return 'invalid';
  // Nome escolhido da planilha sem os dois campos ainda não pode ir.
  if (value === null) return mode === 'decide' ? 'invalid' : null;
  return { typed: value };
}

/**
 * O corpo de `POST .../profile` a partir do rascunho e, se ele ainda não pode ir, o motivo. Escolha
 * de campo que já não espera decisão (a checagem mudou) não vai. `body` é `null` sem decisão.
 */
export function draftBody(
  check: MiceProfileCheckView,
  draft: ProfileDraft,
): { body: MiceProfileResolve | null; reason: ProfileBlockReason | null } {
  const choices: MiceProfileResolve['choices'] = {};
  const typed: Typed = {};
  let reason: ProfileBlockReason | null = null;
  for (const field of MICE_PROFILE_FIELDS) {
    const decision = fieldDecision(check, draft, field);
    if (decision === 'invalid') reason ??= field;
    else if (decision && 'choice' in decision) choices[field] = decision.choice;
    else if (decision) Object.assign(typed, decision.typed);
  }
  if (Object.keys(choices).length + Object.keys(typed).length === 0) {
    const deciding = MICE_PROFILE_FIELDS.some((field) => rowMode(check, field) === 'decide');
    return { body: null, reason: reason ?? (deciding ? 'choose' : 'fill') };
  }
  return { body: Object.keys(typed).length > 0 ? { choices, typed } : { choices }, reason };
}
